/**
 * src/components/Layout/BottomTabBar.tsx — Mobile bottom tab bar.
 *
 * Sticky 4-tab navigation used on mobile in place of the old hamburger menu.
 * Only visible at viewports <=767px — desktop still uses the horizontal
 * TopNav. Bottom-edge safe-area inset is honored so the bar clears the iOS
 * home indicator on modern iPhones.
 *
 * Role-aware tab sets (WP-ATTICDEV-032):
 *   Customer: Home · [Schedule FAB] · Profile
 *   Driver / Both: Home · Drive · Profile
 *
 * The customer center FAB is "Schedule" — the primary conversion action.
 * Because scheduling has two directions, tapping it opens a small chooser
 * (Send to storage / Bring it home) and routes to /dashboard?schedule=<type>,
 * where DashboardPage opens the scheduling modal. Scanning a crate (to label a
 * freshly-filled bin) lives in the dashboard header now, not the nav, since
 * it's an occasional action, not a primary destination.
 *
 * Active-tab highlight is driven by react-router NavLink isActive callback.
 * Self-gated on auth: returns null when not signed in.
 */

import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Package,
  User,
  Truck,
  LayoutDashboard,
  CalendarPlus,
  PackageOpen,
  X,
} from '../icons';
import { useAuth } from "../../context/AuthContext";
import { useCustomerRole } from "../../hooks/useCustomerRole";

interface TabConfig {
  to: string;
  label: string;
  icon: React.ElementType;
  testId: string;
}

/** Customer-role tab set (default). */
const TABS: TabConfig[] = [
  { to: "/dashboard", label: "Home",    icon: Package, testId: "bottom-nav-home" },
  { to: "/profile",   label: "Profile", icon: User,    testId: "bottom-nav-profile" },
];

/** Driver / dual-role tab set. */
const DRIVER_TABS: TabConfig[] = [
  { to: "/driver/home",   label: "Home",    icon: LayoutDashboard, testId: "bottom-nav-driver-home" },
  { to: "/driver/active", label: "Drive",   icon: Truck,           testId: "bottom-nav-driver-drive" },
  { to: "/profile",       label: "Profile", icon: User,            testId: "bottom-nav-profile" },
];

export function BottomTabBar() {
  const { isAuthenticated } = useAuth();
  const { role, loading } = useCustomerRole();
  // Schedule chooser sheet (customer only) — Send to storage vs Bring it home.
  const [chooserOpen, setChooserOpen] = useState(false);

  if (!isAuthenticated) return null;

  const isDriverRole = !loading && (role === "driver" || role === "both");
  const isAdminRole = !loading && role === "admin";
  // Admin is a desktop-only experience — TopNav carries the full nav set.
  if (isAdminRole) return null;
  const activeTabs = isDriverRole ? DRIVER_TABS : TABS;
  // Customer nav gets the central Schedule FAB. Drivers schedule nothing.
  const showScheduleFab = !isDriverRole;

  return (
    <>
      <nav
        data-testid="bottom-tab-bar"
        className="bottom-tab-bar"
        aria-label="Primary navigation"
      >
        {activeTabs.map((tab, idx) => {
          const Icon = tab.icon;
          const tabNode = (
            <NavLink
              key={tab.to}
              to={tab.to}
              data-testid={tab.testId}
              className={({ isActive }) =>
                isActive ? "bottom-tab bottom-tab--active" : "bottom-tab"
              }
              end={false}
            >
              <Icon size={26} aria-hidden="true" className="bottom-tab__icon" />
              <span className="bottom-tab__label">{tab.label}</span>
            </NavLink>
          );
          // Insert the FAB between the first and remaining tabs of the
          // customer set (Home · [Schedule] · Profile).
          if (showScheduleFab && idx === 0) {
            return (
              <React.Fragment key={`${tab.to}-group`}>
                {tabNode}
                <ScheduleFab onClick={() => setChooserOpen(true)} />
              </React.Fragment>
            );
          }
          return tabNode;
        })}
      </nav>
      {chooserOpen && <ScheduleChooserSheet onClose={() => setChooserOpen(false)} />}
    </>
  );
}

// -- Central schedule FAB ----------------------------------------------------
//
// Circular primary-color button in the middle of the customer tab row, lifted
// above the bar so it reads as the primary "action." Opens the schedule chooser.

function ScheduleFab({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="bottom-nav-schedule"
      aria-label="Schedule a pickup or return"
      title="Schedule"
      className="bottom-tab"
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 52, height: 52, borderRadius: '50%',
          background: 'var(--color-primary)', color: 'var(--attic-on-espresso)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginTop: -20, boxShadow: '0 4px 10px rgba(60, 40, 20, 0.22)',
        }}
      >
        <CalendarPlus size={25} strokeWidth={2.2} />
      </span>
      <span className="bottom-tab__label" style={{ marginTop: 2 }}>Schedule</span>
    </button>
  );
}

// -- Schedule chooser sheet --------------------------------------------------
//
// Scheduling has two directions, so the FAB asks which before routing. Each
// choice navigates to /dashboard?schedule=<type>; DashboardPage reads the param
// and opens its scheduling modal (which owns customer/address context).

function ScheduleChooserSheet({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const choose = (type: 'pickup' | 'delivery') => {
    onClose();
    navigate(`/dashboard?schedule=${type}`);
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Schedule"
      data-testid="schedule-chooser"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(35,24,15,0.42)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)',
          borderTopLeftRadius: 'var(--radius-sheet)', borderTopRightRadius: 'var(--radius-sheet)',
          padding: 'var(--space-5) var(--space-4)',
          paddingBottom: 'calc(var(--space-5) + var(--bottom-tab-height) + env(safe-area-inset-bottom, 0px))',
          boxShadow: 'var(--shadow-sheet)',
          animation: 'sessionCardIn 0.24s cubic-bezier(0.2,0.8,0.2,1) both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-xl)', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>
            What would you like to schedule?
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 4, minWidth: 36, minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <ChooserOption
            testId="schedule-choose-pickup"
            icon={<Truck size={22} strokeWidth={2} />}
            tint="var(--color-primary)"
            title="Send crates to storage"
            body="We'll come pick them up."
            onClick={() => choose('pickup')}
          />
          <ChooserOption
            testId="schedule-choose-return"
            icon={<PackageOpen size={22} strokeWidth={2} />}
            tint="var(--color-navy)"
            title="Bring crates home"
            body="We'll deliver them back to you."
            onClick={() => choose('delivery')}
          />
        </div>
      </div>
    </div>
  );
}

function ChooserOption({
  testId, icon, tint, title, body, onClick,
}: {
  testId: string; icon: React.ReactNode; tint: string; title: string; body: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 'var(--space-3)', width: '100%',
        padding: 'var(--space-4)', borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)', background: 'var(--color-surface)',
        boxShadow: 'var(--shadow-1)', cursor: 'pointer', textAlign: 'left',
        fontFamily: 'inherit',
      }}
    >
      <span style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--attic-on-espresso)', background: tint }}>
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--color-text-primary)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{body}</span>
      </span>
    </button>
  );
}
